import {ReactElement, useContext, useMemo} from 'react';

import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import AccountBoxIcon from '@mui/icons-material/AccountBox';
import AllInboxIcon from '@mui/icons-material/AllInbox';
import BarChartIcon from '@mui/icons-material/BarChart';
import CampaignIcon from '@mui/icons-material/Campaign';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import GroupsIcon from '@mui/icons-material/Groups';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import SettingsIcon from '@mui/icons-material/Settings';
import {useTheme} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

import {CompanyContext} from '../../../../../contexts/CompanyContext/CompanyContext';
import {FeatureFlagContext} from '../../../../../contexts/FeatureFlagContext/FeatureFlagContext';
import {workforceMenuItems} from '../../../../../features/company/workforce/navigation/workforceRoutes';
import {AppConfig} from '../../../../../lib/AppConfig';
import {AppRouteResource} from '../../../../../types/AppRouteResource';
import {ModuleTypes} from '../../../../../types/CompanyProductConfiguration/CompanyModule';
import {isCompanyModuleActive} from '../../../../../utils/companyModuleUtils/isCompanyModuleActive';
import {AvatarDropDownMenu} from '../../../../AvatarDropDownMenu/AvatarDropDownMenu';
import {DropdownMenu} from '../../../../DropdownMenu/DropdownMenu';
import {AppHeaderMenuItem} from '../../AppHeaderMenuItem/AppHeaderMenuItem';
import {AppHeaderWrapper} from '../../AppHeaderWrapper';
import {SideDrawerWrapper} from '../../SideDrawerWrapper';

const profileRoute: AppRouteResource = {
  label: 'appHeader.menuLabels.profile',
  icon: <AccountBoxIcon />,
  navigationPath: AppConfig.COMPANY_PROFILE_PATH,
};

const cultureRoutes: AppRouteResource[] = [
  {
    label: 'appHeader.menuLabels.companyAdmin.culture.analytics',
    icon: <QueryStatsIcon />,
    navigationPath: AppConfig.COMPANY_ADMIN_CULTURE_ANALYTICS_PATH,
  },
  {
    label: 'appHeader.menuLabels.companyAdmin.culture.surveys',
    icon: <BarChartIcon />,
    navigationPath: AppConfig.COMPANY_ADMIN_CULTURE_SURVEYS_PATH,
  },
];

const recruitingRoutes: AppRouteResource[] = [
  {
    label: 'appHeader.menuLabels.companyAdmin.recruiting.analytics',
    icon: <QueryStatsIcon />,
    navigationPath: AppConfig.COMPANY_ANALYTICS_DASHBOARD_PATH,
  },
  {
    label: 'campaignsPage.header',
    icon: <CampaignIcon />,
    navigationPath: AppConfig.COMPANY_CAMPAIGNS_PATH,
  },
  {
    label: 'appHeader.menuLabels.companyAdmin.recruiting.matches',
    icon: <GroupsIcon />,
    navigationPath: AppConfig.COMPANY_ADMIN_RECRUITING_MATCHES_PATH,
  },
  {
    label: 'appHeader.menuLabels.companyAdmin.recruiting.talentPool',
    icon: <FolderSharedIcon />,
    navigationPath: AppConfig.COMPANY_ADMIN_RECRUITING_TALENT_POOL_PATH,
  },
  {
    label: 'appHeader.menuLabels.cultureCheck',
    icon: <PersonSearchIcon />,
    navigationPath: AppConfig.COMPANY_CULTURE_CHECK_PATH,
  },
];

const userRoutes: AppRouteResource[] = [
  {
    label: 'appHeader.menuLabels.settings',
    icon: <SettingsIcon />,
    navigationPath: AppConfig.ACCOUNT_SETTINGS_PATH,
  },
  {
    label: 'appHeader.menuLabels.logout',
    icon: <LogoutIcon />,
    navigationPath: AppConfig.LOGOUT_PATH,
  },
];

export const FullAccessHeader = () => {
  const {t} = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const {flags, isLoading} = useContext(FeatureFlagContext);
  const {productConfiguration} = useContext(CompanyContext);

  const isWorkforceModuleActive = isCompanyModuleActive(
    ModuleTypes.WorkforceModule,
    productConfiguration.data?.companyModules
  );

  const recruitingRoutesWithFlags = useMemo(() => {
    const routes = [...recruitingRoutes];

    if (flags.JOB_POSTING_PAGE) {
      routes.push({
        label: 'appHeader.menuLabels.companyAdmin.recruiting.jobPostings',
        icon: <DescriptionIcon />,
        navigationPath: AppConfig.COMPANY_ADMIN_RECRUITING_JOB_POSTINGS_PATH,
      });
    }

    if (flags.INBOX_PAGE) {
      routes.push({
        label: 'appHeader.menuLabels.companyAdmin.recruiting.inbox',
        icon: <AllInboxIcon />,
        navigationPath: AppConfig.COMPANY_ADMIN_RECRUITING_INBOX_PATH,
      });
    }

    return routes;
  }, [flags.JOB_POSTING_PAGE, flags.INBOX_PAGE]);

  const desktopHeaderItems = useMemo(() => {
    const items: ReactElement[] = [
      <DropdownMenu key='culture' buttonLabel={t('appHeader.moduleLabels.culture')}>
        {cultureRoutes.map((route) => (
          <AppHeaderMenuItem
            key={route.label}
            label={t(route.label)}
            onClick={() => navigate(route.navigationPath)}
            icon={route.icon}
          />
        ))}
      </DropdownMenu>,
      <DropdownMenu key='recruiting' buttonLabel={t('appHeader.moduleLabels.recruiting')}>
        {recruitingRoutesWithFlags.map((route) => (
          <AppHeaderMenuItem
            key={route.label}
            label={t(route.label)}
            onClick={() => navigate(route.navigationPath)}
            icon={route.icon}
          />
        ))}
      </DropdownMenu>,
    ];

    if (isWorkforceModuleActive) {
      items.push(
        <DropdownMenu key='workforce' buttonLabel={t('appHeader.moduleLabels.workforce')}>
          {workforceMenuItems.map((route) => (
            <AppHeaderMenuItem
              key={route.label}
              label={t(route.label)}
              onClick={() => navigate(route.navigationPath)}
              icon={route.icon}
            />
          ))}
        </DropdownMenu>
      );
    }

    items.push(<AvatarDropDownMenu key='avatar' />);

    return items;
  }, [isWorkforceModuleActive, navigate, recruitingRoutesWithFlags, t]);

  if (isLoading) {
    return null;
  }

  if (isSmallScreen) {
    return (
      <SideDrawerWrapper
        sideBarSections={[
          {menuItems: [profileRoute]},
          {
            title: 'appHeader.moduleLabels.culture',
            menuItems: cultureRoutes,
          },
          {
            title: 'appHeader.moduleLabels.recruiting',
            menuItems: recruitingRoutesWithFlags,
          },
          ...(isWorkforceModuleActive
            ? [
              {
                title: 'appHeader.moduleLabels.workforce',
                menuItems: workforceMenuItems,
              },
            ]
            : []),
          {menuItems: userRoutes},
        ]}
      />
    );
  }

  return <AppHeaderWrapper isTransparent={false}>{desktopHeaderItems}</AppHeaderWrapper>;
};
